'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box } from '@chakra-ui/react'
import { Text } from '@chakra-ui/react'
import { Region } from '@leid/api-locations'
import { useFormik } from 'formik'
import { useRouter } from 'next/navigation'
import * as Yup from 'yup'

import { MissingProductsForm } from 'api/client/models/missingProducts'
import { CustomerInfo } from 'app/utils/authentication'
import { ErrorMessage } from 'components/form/ErrorMessage/styles'
import { Input } from 'components/form/Input'
import { RegionsList } from 'components/partials/shared/ProductInfoAndRentalSection/components/RentalSection/components/LocationsSelector/RegionsList'
import { Button } from 'components/ui/Buttons/Button'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import { RegionWithAvailability } from '../../products/ProductInfoAndRentalSection/components/RentalSection/components/LocationsSelector'

import { getFormInitialValues, SendProductRequestFormData } from './form'

type Props = {
  searchTerm?: string
  customerInfo: CustomerInfo
}

export const SendProductRequest = ({ searchTerm, customerInfo }: Props): JSX.Element => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['common'])

  const router = useRouter()

  const isAuthenticated = customerInfo.customer !== null

  const [regions, setRegions] = useState<RegionWithAvailability[]>([])
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>(undefined)

  useEffect(() => {
    populateRegions()
  }, [])

  const validationSchema = Yup.object().shape({
    email: Yup.string().when('isAuthenticated', {
      is: false,
      then: Yup.string().email(t('common:invalidEmail')).required(t('common:required')),
      otherwise: undefined,
    }),
    fullName: Yup.string().when('isAuthenticated', {
      is: false,
      then: Yup.string().required(t('common:required')),
      otherwise: undefined,
    }),
    phoneNumber: Yup.number().when('isAuthenticated', {
      is: false,
      then: Yup.number()
        .test('len', t('common:phoneNumberValidation'), (val) => (val ? val.toString().length === 8 : false))
        .required(t('common:required'))
        .typeError(t('common:validationNumber')),
      otherwise: undefined,
    }),
    description: Yup.string().required(t('common:required')),
    regionId: Yup.number().required(t('common:required')),
  })

  const onActualSubmit = async (formValues: SendProductRequestFormData) => {
    const request: MissingProductsForm = {
      fullName: formValues.fullName,
      email: formValues.email,
      phoneNumber: formValues.phoneNumber,
      description: formValues.description,
      regionId: formValues.regionId ?? 0,
      regionName: selectedRegion?.name ?? '',
    }
    const result = await apiClient.sendMissingProductsForm(request)
    if (!result.success || !result.data) {
      dispatch(displaySnackbar({ message: t('common:missingProductsFormError'), isError: true }))
      return
    }

    dispatch(displaySnackbar({ message: t('common:requestWasSent'), isError: false }))
    router.push('/browse')
  }

  const { getFieldProps, touched, errors, handleSubmit, setFieldValue, values, validateForm } =
    useFormik<SendProductRequestFormData>({
      initialValues: getFormInitialValues(searchTerm ?? '', customerInfo),
      onSubmit: onActualSubmit,
      validationSchema: validationSchema,
    })

  useEffect(() => {
    /**
     * Force validation
     */
    validateForm()
  }, [validateForm, values])

  const populateRegions = async (): Promise<void> => {
    const response = await apiClient.getRegions({ active: true, allData: true }, undefined, {
      latitudeCoordinate: customerInfo.customer?.coordinates?.latitude,
      longitudeCoordinate: customerInfo.customer?.coordinates?.longitude,
    })

    if (response.success && response.data) {
      setRegions(response.data.rows.map<RegionWithAvailability>((s) => ({ ...s, hasAvailableLocations: true })))
    }
  }

  const onRegionChange = (selectedRegion: Region | undefined) => {
    const regionId = selectedRegion?.id ?? undefined
    setFieldValue('regionId', regionId)
    setSelectedRegion(selectedRegion)
  }

  return (
    <Box padding={isAuthenticated ? 0 : 5}>
      {!isAuthenticated && (
        <Box>
          <Box>
            <Input
              {...getFieldProps('email')}
              placeholder={t('common:email')}
              name="email"
              touched={touched.email}
              error={errors.email}
              label={t('common:email')}
            />
          </Box>
          <Box marginTop="20px">
            <Input
              {...getFieldProps('phoneNumber')}
              placeholder={t('common:phoneNumber')}
              name="phoneNumber"
              touched={touched.phoneNumber}
              error={errors.phoneNumber}
              type="number"
              label={t('common:phoneNumber')}
              adornmentLeft="+47"
              paddingLeft="35px"
            />
          </Box>
          <Box marginTop="20px">
            <Input
              {...getFieldProps('fullName')}
              placeholder={t('common:fullName')}
              name="fullName"
              touched={touched.fullName}
              error={errors.fullName}
              label={t('common:fullName')}
            />
          </Box>
        </Box>
      )}

      <Box marginTop={!isAuthenticated ? '20px' : undefined}>
        <Input
          {...getFieldProps('description')}
          type="textarea"
          placeholder={t('common:description')}
          name="description"
          value={values.description}
          touched={touched.description}
          error={errors.description}
          label={t('common:description')}
        />
      </Box>

      <Box marginTop="20px">
        <Text color="green.a200" marginBottom="5px">
          {t('regions')}
        </Text>
        <RegionsList title="" regions={regions} selectedRegionId={values.regionId} onRegionChange={onRegionChange} />
        <ErrorMessage>{touched.regionId ? errors.regionId : ''}</ErrorMessage>
      </Box>

      <Box marginTop="20px" marginBottom="64px">
        <Button onClick={handleSubmit}>{t('common:requestQuotationButton')}</Button>
      </Box>
    </Box>
  )
}
